import { useState } from "react";
import { Building2, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export function ContactSalesDialog({ open, onOpenChange, source = "pricing_enterprise" }) {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setSending(true);
    try {
      await api.post("/leads", { ...form, source });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(false); setForm({ name: "", email: "", organization: "", message: "" }); }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md" data-testid="contact-sales-dialog">
        {done ? (
          <div className="py-6 text-center" data-testid="contact-sales-success">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--processor-success))]/15 text-[hsl(var(--processor-success))]">
              <Check size={24} />
            </div>
            <h3 className="mt-4 font-display text-xl font-semibold">Thanks — we'll be in touch.</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Our team will reach out about an Espresso Enterprise deployment shortly.
            </p>
            <Button onClick={() => close(false)} className="mt-6 rounded-sm" data-testid="contact-sales-close">
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-display">
                <Building2 size={18} className="text-primary" /> Talk to sales
              </DialogTitle>
              <DialogDescription>
                Tell us about your organization and we'll tailor an Enterprise plan.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="contact-sales-form">
              <Input placeholder="Your name" value={form.name} onChange={set("name")} className="rounded-sm" data-testid="contact-name" />
              <Input type="email" placeholder="Work email" value={form.email} onChange={set("email")} className="rounded-sm" data-testid="contact-email" />
              <Input placeholder="Organization (optional)" value={form.organization} onChange={set("organization")} className="rounded-sm" data-testid="contact-org" />
              <Textarea placeholder="What are you looking to build? (optional)" value={form.message} onChange={set("message")} rows={3} className="rounded-sm" data-testid="contact-message" />
              <Button type="submit" disabled={sending} className="w-full rounded-sm" data-testid="contact-submit">
                {sending ? "Sending…" : "Send enquiry"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
